import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Card,
} from 'react-bootstrap';
import IconAlert from '@/components/common/IconAlert';
import { indexerAlertVariant } from '../lib/indexer-alert-variant';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { navigateToIndexWithPrefill, navigateToInspectWithPrefill } from '../lib/url-list-prefill';
import { getIndexerErrorInfo } from '../api/errors';
import {
  useInspectUrlsMutation,
  useLazyListProjectUrlsQuery,
  useSyncProjectUrlsFromSitemapMutation,
} from '../api/indexerApi';
import type { UrlInventoryRow, UrlListStatusFilter } from '../api/types';
import { NeedProject } from '../components/NeedProject';
import { IndexerKpiCard, type IndexerKpiTone } from '../components/IndexerKpiCard';
import {
  IndexerTablePreload,
  UrlListPagePreload,
} from '../components/preloads/IndexerPreloads';
import { UrlInventoryTable } from '../components/UrlInventoryTable';
import { UrlListToolbar } from '../components/UrlListToolbar';
import { ExcludedBucketHelpTip } from '../components/ExcludedBucketHelpTip';
import {
  coverageLabelForChip,
  parseStatsDaysParam,
  parseUrlListStatus,
} from '../lib/drilldown-links';
import { useProject } from '../lib/project-context';
import { INSPECT_MAX_URLS_PER_JOB } from '../lib/inspect-limits';
import { statsRangeHint, type StatsRange } from '../lib/job-range';
import { copyText, formatFaNumber } from '../lib/utils';

/** Backend max; load in chunks then scroll inside the grid (no page UI). */
const FETCH_PAGE_SIZE = 200;
const INDEX_BULK_LIMIT = 200;

export function UrlListPage() {
  const { project, projectRef } = useProject();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<UrlInventoryRow[]>([]);
  const [summary, setSummary] = useState({
    total: 0,
    indexed: 0,
    notIndexed: 0,
    error: 0,
    neverInspected: 0,
    statusChanged: 0,
  });
  const status = parseUrlListStatus(searchParams.get('status'));
  const coverage = searchParams.get('coverage')?.trim() || '';
  const days = parseStatsDaysParam(searchParams.get('days'));
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sort, setSort] = useState('last_inspect');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ variant: string; text: string } | null>(
    null,
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [listProjectUrls] = useLazyListProjectUrlsQuery();
  const [syncProjectUrlsFromSitemap] = useSyncProjectUrlsFromSitemapMutation();
  const [inspectUrls] = useInspectUrlsMutation();

  const patchParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setStatus = useCallback(
    (next: UrlListStatusFilter) => {
      patchParams((params) => {
        if (next === 'all') params.delete('status');
        else params.set('status', next);
        params.delete('coverage');
      });
    },
    [patchParams],
  );

  const clearCoverage = useCallback(() => {
    patchParams((params) => {
      params.delete('coverage');
    });
  }, [patchParams]);

  const clearDays = useCallback(() => {
    patchParams((params) => {
      params.delete('days');
    });
  }, [patchParams]);

  const load = useCallback(async () => {
    if (!project || !projectRef) return;
    setLoading(true);
    setMessage(null);
    try {
      const all: UrlInventoryRow[] = [];
      let page = 1;
      let totalCount = 0;
      let nextSummary = {
        total: 0,
        indexed: 0,
        notIndexed: 0,
        error: 0,
        neverInspected: 0,
        statusChanged: 0,
      };

      for (;;) {
        const data = await listProjectUrls(
          {
            projectId: projectRef,
            q: search || undefined,
            status,
            coverage: coverage || undefined,
            days,
            sort,
            order: 'desc',
            page,
            pageSize: FETCH_PAGE_SIZE,
          },
          true,
        ).unwrap();

        nextSummary = data.summary;
        totalCount = data.total;
        all.push(...(data.items ?? []));

        if (all.length >= totalCount || (data.items?.length ?? 0) < FETCH_PAGE_SIZE) {
          break;
        }
        page += 1;
      }

      setItems(all);
      setSummary(nextSummary);
      setSelected(new Set());
    } catch (err) {
      setMessage({
        variant: 'danger',
        text: getIndexerErrorInfo(err).message || 'بارگذاری لیست ناموفق بود',
      });
    } finally {
      setLoading(false);
    }
  }, [project, projectRef, search, status, coverage, days, sort, listProjectUrls]);

  useEffect(() => {
    void load();
  }, [load]);

  const selectedUrls = useMemo(() => [...selected], [selected]);

  async function syncSitemap() {
    if (!project || !projectRef) return;
    setSyncing(true);
    setMessage(null);
    try {
      const result = await syncProjectUrlsFromSitemap(projectRef).unwrap();
      setMessage({
        variant: 'success',
        text: `${formatFaNumber(result.scanned)} URL اسکن شد؛ ${formatFaNumber(result.added)} مورد جدید به لیست اضافه شد.`,
      });
      await load();
    } catch (err) {
      setMessage({
        variant: 'danger',
        text:
          getIndexerErrorInfo(err).message || 'همگام‌سازی sitemap ناموفق بود',
      });
    } finally {
      setSyncing(false);
    }
  }

  function sendToIndex(urls: string[]) {
    const slice = urls.slice(0, INDEX_BULK_LIMIT);
    navigateToIndexWithPrefill(navigate, slice, 'url-list-bulk');
  }

  function sendToInspect(urls: string[]) {
    const slice = urls.slice(0, INSPECT_MAX_URLS_PER_JOB);
    navigateToInspectWithPrefill(navigate, slice, 'url-list-bulk');
  }

  async function inspectSelected() {
    if (!project || !projectRef || selectedUrls.length === 0) return;
    setBusy(true);
    try {
      await inspectUrls({
        projectId: projectRef,
        urls: selectedUrls.slice(0, INSPECT_MAX_URLS_PER_JOB),
      }).unwrap();
      setMessage({
        variant: 'success',
        text: `${formatFaNumber(Math.min(selectedUrls.length, INSPECT_MAX_URLS_PER_JOB))} آدرس به صف بازرسی رفت.`,
      });
      await load();
    } catch (err) {
      setMessage({
        variant: 'danger',
        text: getIndexerErrorInfo(err).message || 'ارسال بازرسی ناموفق بود',
      });
    } finally {
      setBusy(false);
    }
  }

  function exportCsv() {
    const rows = selectedUrls.length > 0 ? selectedUrls : items.map((r) => r.url);
    const body = rows.join('\n');
    const blob = new Blob([body], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `urls-${project?.id ?? 'export'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!project || !projectRef) return <NeedProject />;

  if (loading && items.length === 0) {
    return <UrlListPagePreload />;
  }

  return (
    <div className="d-flex flex-column gap-3">
      <div className="overview-kpi-grid">
        {(
          [
            { label: 'همه', value: summary.total, tone: 'neutral' as IndexerKpiTone, filter: 'all' as const },
            { label: 'ایندکس‌شده', value: summary.indexed, tone: 'success' as IndexerKpiTone, filter: 'indexed' as const },
            { label: 'ایندکس‌نیست', value: summary.notIndexed, tone: 'warning' as IndexerKpiTone, filter: 'not_indexed' as const },
            { label: 'خطا', value: summary.error, tone: 'danger' as IndexerKpiTone, filter: 'error' as const },
            { label: 'بدون بازرسی', value: summary.neverInspected, tone: 'neutral' as IndexerKpiTone, filter: 'never_inspected' as const },
            {
              label: 'تغییر در آخرین بازرسی',
              value: summary.statusChanged,
              tone: 'primary' as IndexerKpiTone,
              filter: 'status_changed' as const,
            },
          ] as const
        ).map((card, index) => (
          <IndexerKpiCard
            key={card.label}
            tone={card.tone}
            imageIndex={index}
            className={`overview-kpi-card--clickable${
              status === card.filter && !coverage
                ? ' overview-kpi-card--selected'
                : ''
            }`}
            role="button"
            tabIndex={0}
            onClick={() => setStatus(card.filter)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setStatus(card.filter);
              }
            }}
          >
            <div className="overview-kpi-value">
              {formatFaNumber(card.value)}
            </div>
            <div className="text-secondary small d-inline-flex align-items-center gap-1">
              {card.label}
              {card.filter === 'not_indexed' && (
                <ExcludedBucketHelpTip placement="top" />
              )}
            </div>
          </IndexerKpiCard>
        ))}
      </div>

      {(coverage || days) && (
        <div className="d-flex flex-wrap align-items-center gap-2">
          {coverage && (
            <Badge bg="primary" className="d-inline-flex align-items-center gap-2 py-2 px-3">
              coverage: {coverageLabelForChip(coverage)}
              <button
                type="button"
                className="btn-close btn-close-white"
                style={{ fontSize: '0.55rem' }}
                aria-label="حذف فیلتر coverage"
                onClick={clearCoverage}
              />
            </Badge>
          )}
          {days && (
            <Badge bg="secondary" className="d-inline-flex align-items-center gap-2 py-2 px-3">
              بازه: {statsRangeHint(days as StatsRange)}
              <button
                type="button"
                className="btn-close btn-close-white"
                style={{ fontSize: '0.55rem' }}
                aria-label="حذف فیلتر بازه"
                onClick={clearDays}
              />
            </Badge>
          )}
        </div>
      )}

      {message && (
        <IconAlert
          variant={indexerAlertVariant(message.variant)}
          dismissible
          onClose={() => setMessage(null)}
        >
          <p className="mb-0 text-justify pe-3">{message.text}</p>
        </IconAlert>
      )}

      <Card>
        <Card.Header className="bg-body px-2 py-2">
          <UrlListToolbar
            searchInput={searchInput}
            onSearchInputChange={setSearchInput}
            onSearchSubmit={() => setSearch(searchInput.trim())}
            onSearchClear={() => {
              setSearchInput('');
              setSearch('');
              setPage(1);
            }}
            sort={sort}
            onSortChange={setSort}
            syncing={syncing}
            onSyncSitemap={() => void syncSitemap()}
            busy={busy}
            selectedCount={selectedUrls.length}
            status={status}
            statusChangedCount={summary.statusChanged}
            onStatusChange={setStatus}
            onSendSelectedIndex={() => sendToIndex(selectedUrls)}
            onInspectSelected={() => void inspectSelected()}
            onCopySelected={() => void copyText(selectedUrls.join('\n'))}
            onExportTxt={exportCsv}
            onSendNotIndexedPage={() =>
              sendToIndex(
                items
                  .filter(
                    (r) =>
                      r.indexBucket === 'excluded' || r.indexBucket === 'error',
                  )
                  .map((r) => r.url),
              )
            }
            onInspectAllPage={() => sendToInspect(items.map((r) => r.url))}
          />
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <IndexerTablePreload rows={8} cols={5} />
          ) : (
            <UrlInventoryTable
              projectId={projectRef}
              items={items}
              selected={selected}
              onSelectedChange={setSelected}
            />
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
